import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { useIsMobile } from "../shared/utils";
import { RectificationWorkModal, ServiceRequestWorkModal } from "./FieldTechWorkModal";
import { InspectionSheet } from "../inspections/InspectionSheet";

export function FieldTechCompleted() {
  const [rectRequests, setRectRequests] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [completedInspections, setCompletedInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workJob, setWorkJob] = useState(null);
  const [srWorkJob, setSrWorkJob] = useState(null);
  const [openInspectionId, setOpenInspectionId] = useState(null);
  const isMobile = useIsMobile();

  const fetchCompleted = async () => {
    try {
      const [rrRes, srRes, inspRes] = await Promise.all([
        axios.get(`${API}/rectification-requests/`),
        axios.get(`${API}/service-requests/?include_completed=true`),
        axios.get(`${API}/inspections/`),
      ]);
      const completed = rrRes.data
        .filter((rr) => rr.status === "completed")
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      setRectRequests(completed);

      const completedSRs = srRes.data
        .filter((sr) => sr.status === "complete")
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      setServiceRequests(completedSRs);

      const completedInsps = inspRes.data
        .filter((i) => i.status !== "in_progress" && !i.locked)
        .sort((a, b) => new Date(b.completed_at || b.date) - new Date(a.completed_at || a.date));
      setCompletedInspections(completedInsps);
    } catch (e) {
      console.error("Failed to load completed jobs:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCompleted(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openRectWork = (rr) => {
    setWorkJob({
      type: "rectification",
      raw: rr,
      site_name: rr.quote_detail?.inspection_site_name || "—",
      site_address: rr.quote_detail?.inspection_site_address || "",
      detail: `${(rr.items || []).length} item${(rr.items || []).length !== 1 ? "s" : ""}`,
      status: rr.status,
    });
  };

  const openSRWork = (sr) => {
    setSrWorkJob({
      raw: sr,
      site_name: sr.site_name || "—",
      site_address: sr.site_address || "",
    });
  };

  const containerStyle = {
    padding: isMobile ? "16px" : "24px",
    maxWidth: "700px",
    margin: "0 auto",
  };

  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    border: `1px solid ${colors.border}`,
    overflow: "hidden",
    marginBottom: "12px",
  };

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    padding: "14px 16px",
    gap: "12px",
    cursor: "pointer",
  };

  const sectionLabel = (text) => (
    <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: colors.slate, letterSpacing: "0.5px", margin: "20px 0 10px" }}>
      {text}
    </div>
  );

  const openButton = (onClick) => (
    <div style={{ padding: "0 16px 14px", display: "flex", justifyContent: "flex-end" }}>
      <button
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        style={{ padding: "6px 16px", borderRadius: "20px", border: `1px solid ${colors.border}`, backgroundColor: colors.white, color: colors.darkGrey, fontSize: "12px", fontWeight: "600", cursor: "pointer" }}
      >
        Open / Correct
      </button>
    </div>
  );

  const hasAnything = rectRequests.length > 0 || serviceRequests.length > 0 || completedInspections.length > 0;

  if (openInspectionId) {
    return (
      <InspectionSheet
        inspectionId={openInspectionId}
        onBack={() => { setOpenInspectionId(null); fetchCompleted(); }}
      />
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: colors.slate, letterSpacing: "0.5px", marginBottom: "4px" }}>
          Completed Work
        </div>
        <div style={{ fontSize: "22px", fontWeight: "800", color: colors.darkGrey }}>
          Recently Completed
        </div>
        <div style={{ fontSize: "13px", color: colors.slate, marginTop: "2px" }}>
          Tap any job to review or correct actions taken
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: colors.slate, fontSize: "14px" }}>
          Loading…
        </div>
      ) : !hasAnything ? (
        <div style={{ textAlign: "center", padding: "48px 24px", color: colors.slate, fontSize: "14px" }}>
          <div style={{ fontSize: "32px", marginBottom: "12px" }}>✅</div>
          No completed jobs yet
        </div>
      ) : (
        <>
          {/* Completed inspections */}
          {completedInspections.length > 0 && (
            <>
              {sectionLabel("Inspections")}
              {completedInspections.map((insp) => {
                const completedDate = (insp.completed_at || insp.date)
                  ? new Date(insp.completed_at || insp.date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
                  : null;

                return (
                  <div key={insp.id} style={cardStyle} onClick={() => setOpenInspectionId(insp.id)}>
                    <div style={rowStyle}>
                      <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: insp.status === "failed" ? colors.red : "#22c55e", flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "14px", fontWeight: "700", color: colors.darkGrey, marginBottom: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {insp.site_name || "—"}
                        </div>
                        <div style={{ fontSize: "12px", color: colors.slate, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {insp.site_area_name || "Whole Site"}
                        </div>
                        {completedDate && (
                          <div style={{ fontSize: "11px", color: colors.slate, marginTop: "2px" }}>
                            Completed {completedDate}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px", flexShrink: 0 }}>
                        <span style={{
                          fontSize: "11px", fontWeight: "700", padding: "2px 8px", borderRadius: "12px",
                          backgroundColor: insp.status === "failed" ? "#fee2e2" : "#dcfce7",
                          color: insp.status === "failed" ? colors.red : "#15803d",
                        }}>
                          {insp.status === "failed" ? "Failed" : "Passed"}
                        </span>
                      </div>
                    </div>
                    {openButton(() => setOpenInspectionId(insp.id))}
                  </div>
                );
              })}
            </>
          )}

          {/* Rectification requests */}
          {rectRequests.length > 0 && (
            <>
              {sectionLabel("Rectification Work")}
              {rectRequests.map((rr) => {
                const itemCount = (rr.items || []).length;
                const completedDate = rr.updated_at
                  ? new Date(rr.updated_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
                  : null;

                return (
                  <div key={rr.id} style={cardStyle} onClick={() => openRectWork(rr)}>
                    <div style={rowStyle}>
                      <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#22c55e", flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "14px", fontWeight: "700", color: colors.darkGrey, marginBottom: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {rr.quote_detail?.inspection_site_name || "—"}
                        </div>
                        <div style={{ fontSize: "12px", color: colors.slate, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {rr.quote_detail?.inspection_site_address || ""}
                        </div>
                        {completedDate && (
                          <div style={{ fontSize: "11px", color: colors.slate, marginTop: "2px" }}>
                            Completed {completedDate}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px", flexShrink: 0 }}>
                        <span style={{ fontSize: "11px", fontWeight: "700", backgroundColor: "#dcfce7", color: "#15803d", padding: "2px 8px", borderRadius: "12px" }}>
                          Completed
                        </span>
                        <span style={{ fontSize: "11px", color: colors.slate }}>
                          {itemCount} item{itemCount !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                    {openButton(() => openRectWork(rr))}
                  </div>
                );
              })}
            </>
          )}

          {/* Service requests */}
          {serviceRequests.length > 0 && (
            <>
              {sectionLabel("Service Requests")}
              {serviceRequests.map((sr) => {
                const completedDate = sr.updated_at
                  ? new Date(sr.updated_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
                  : null;

                return (
                  <div key={sr.id} style={cardStyle} onClick={() => openSRWork(sr)}>
                    <div style={rowStyle}>
                      <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: colors.amber, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "14px", fontWeight: "700", color: colors.darkGrey, marginBottom: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {sr.site_name || "—"}
                        </div>
                        <div style={{ fontSize: "12px", color: colors.slate, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {sr.site_address || ""}
                        </div>
                        {completedDate && (
                          <div style={{ fontSize: "11px", color: colors.slate, marginTop: "2px" }}>
                            Completed {completedDate}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px", flexShrink: 0 }}>
                        <span style={{ fontSize: "11px", fontWeight: "700", backgroundColor: "#fff7ed", color: colors.amber, padding: "2px 8px", borderRadius: "12px" }}>
                          SR
                        </span>
                      </div>
                    </div>
                    {openButton(() => openSRWork(sr))}
                  </div>
                );
              })}
            </>
          )}
        </>
      )}

      {/* Work modals */}
      {workJob && (
        <RectificationWorkModal
          job={workJob}
          onClose={() => setWorkJob(null)}
          onSaved={fetchCompleted}
        />
      )}
      {srWorkJob && (
        <ServiceRequestWorkModal
          job={srWorkJob}
          onClose={() => setSrWorkJob(null)}
          onSaved={fetchCompleted}
        />
      )}
    </div>
  );
}
